import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import createApp from '@shopify/app-bridge';
import { getSessionToken } from '@shopify/app-bridge-utils';

const ShopifyBridgeContext = createContext(null);

export function useShopifyBridge() {
  return useContext(ShopifyBridgeContext);
}

export function ShopifyBridgeProvider({ host, shop, children }) {
  const [bridgeApp, setBridgeApp] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!host) return;

    try {
      const app = createApp({
        apiKey: process.env.REACT_APP_SHOPIFY_API_KEY,
        host: host,
        forceRedirect: true,
      });
      setBridgeApp(app);
      setReady(true);
    } catch (e) {
      /* logged */
      setReady(true); // Still render the app even if bridge fails
    }
  }, [host]);

  // Authenticated fetch that includes session token
  const authenticatedFetch = useCallback(async (url, options = {}) => {
    if (!bridgeApp) {
      return fetch(url, options);
    }

    try {
      const token = await getSessionToken(bridgeApp);
      const headers = {
        ...options.headers,
        'Authorization': `Bearer ${token}`,
      };
      return fetch(url, { ...options, headers });
    } catch (e) {
      /* logged */
      return fetch(url, options);
    }
  }, [bridgeApp]);

  const value = {
    app: bridgeApp,
    shop: shop,
    host: host,
    isEmbedded: true,
    ready,
    authenticatedFetch,
  };

  if (!ready) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <div style={{ textAlign: 'center', color: '#666' }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Connecting to Shopify...</div>
          <div style={{ width: 32, height: 32, border: '3px solid #e5e7eb', borderTopColor: '#16a34a', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto' }} />
        </div>
      </div>
    );
  }

  return (
    <ShopifyBridgeContext.Provider value={value}>
      {children}
    </ShopifyBridgeContext.Provider>
  );
}
